// ui--flow-export — deterministic schematic export (wave-2 system map, G7).
// One chrome controller rendering inside a PanelComponent composition (the
// dnd_palette_filter pattern: dedicated controller, one responsibility),
// wired to the engine through the ui--flow outlet.
//
// The controller is the DOM/token half of the feature; the SVG itself is
// built by the pure lib in app/javascript/lib/flow/flow_export.js from
// engine data (toObject() defs) + geometry reads (measured node boxes, live
// edge-path `d`s) + resolved computed-style tokens — never a DOM clone, so
// the export is canonical by construction: no chrome, no viewer state, no
// selection/highlight leakage.
//
// Outputs: .svg download; raster PNG (SVG → Image → canvas → toBlob) with
// clipboard copy where supported; and a 1200×630 share card (title band +
// map + subtitle from the host value or the page's meta description) via
// download + clipboard. The toolbar's "Export JSON" (toObject() → file)
// remains the lossless export — this surface is the shareable picture.
import { Controller } from "@hotwired/stimulus"
import {
  serializeFlowSvg,
  serializeShareCard,
  serializeDualThemeFlowSvg,
  validateFlowSvg,
  validateShareCard,
  flowSvgSize,
  contentBounds,
  cameraEndpoints,
  cameraPath,
  CARD_WIDTH,
  CARD_HEIGHT,
  EXPORT_PADDING,
  WEBM_DURATION_MS,
  WEBM_FPS
} from "../../../lib/flow/flow_export"

export default class extends Controller {
  static outlets = ["ui--flow"]
  static targets = ["readout"]
  static values = {
    title: String,
    subtitle: String,
    // PNG renders at 2× the SVG's pixel size so the raster stays crisp on
    // retina screens; the share card ignores this (1200×630 is the card).
    pngScale: { type: Number, default: 2 },
    // Opt-in render-quality gate (wave-3 W3-C): when set, every export
    // action validates its artifact against the snapshot it was built from
    // and appends the verdict (PASS, or FAIL with every reason) to the
    // readout. The gate checks the artifact, never graph semantics.
    validate: Boolean
  }

  // ── Actions (data-action is the single wiring source) ────────────────

  downloadSvg() {
    const snap = this._snapshot()
    if (!snap) return
    const svg = serializeFlowSvg({ ...snap, title: this._title() })
    this._download(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }), "flow.svg")
    this._say(`Exported flow.svg — ${this._counts(snap)}.${this._gateSuffix("flow", snap, svg)}`)
  }

  // Gate-only action (the quality_gate scenario): validates the serialized
  // artifact against the very snapshot it was built from and reports PASS,
  // or FAIL with every reason the pure validator returned. The RED half of
  // the demo is driven by corrupting a live snapshot and re-reporting —
  // `ctrl._reportGate(corruptedSnap)` from a console/eval — so the UI never
  // ships a deliberately-broken fixture.
  validateExport() {
    const snap = this._snapshot()
    if (!snap) return
    this._reportGate(snap)
  }

  // Dual-theme SVG (wave-3 W3-C): ONE file, light + dark variant groups
  // with identical geometry, flipped by an embedded media query. The
  // opposite theme's tokens resolve on the same live elements through a
  // synchronous root-marker flip + restore (see _oppositeSnapshot).
  downloadDualSvg() {
    const light = this._snapshot()
    if (!light) return
    const dark = this._oppositeSnapshot()
    if (!dark) {
      this._say("Dual-theme export needs a measurable graph — nothing to export.")
      return
    }
    const svg = serializeDualThemeFlowSvg({ light, dark, title: this._title() })
    this._download(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }), "flow-dual.svg")
    this._say(`Exported flow-dual.svg — light+dark variants in one file, ${this._counts(light)}.`)
  }

  // Animated schematic export (wave-3 W3-C): serialize → rasterize →
  // canvas.captureStream + MediaRecorder over a scripted deterministic
  // camera ease (fit → WEBM_ZOOM on the first visible node, ~4s) →
  // flow-motion.webm. Feature-detect uses the same honest fallback grammar
  // as the clipboard actions.
  async downloadWebm() {
    const snap = this._snapshot()
    if (!snap) return
    const probe = document.createElement("canvas")
    if (typeof MediaRecorder === "undefined" || typeof probe.captureStream !== "function") {
      this._say("Animated WebM export is not supported in this browser — use the SVG or PNG buttons.")
      return
    }
    const { width, height } = flowSvgSize(snap)
    const svg = serializeFlowSvg({ ...snap, title: this._title() })
    const canvas = document.createElement("canvas")
    canvas.width = Math.round(width * this.pngScaleValue)
    canvas.height = Math.round(height * this.pngScaleValue)
    const ctx = canvas.getContext("2d")
    if (!ctx) {
      this._say("WebM export failed in this browser — the SVG download still works.")
      return
    }
    try {
      const img = await this._loadImage(svg)
      const endpoints = this._cameraEndpoints(snap, canvas)
      const blob = await this._record(canvas, ctx, img, endpoints)
      this._download(blob, "flow-motion.webm")
      this._say(`Exported flow-motion.webm — ${WEBM_DURATION_MS / 1000}s camera move over ${this._counts(snap)}.`)
    } catch (e) {
      this._say("WebM export failed in this browser — the SVG download still works.")
    }
  }

  async downloadPng() {
    const snap = this._snapshot()
    if (!snap) return
    const { width, height } = flowSvgSize(snap)
    const svg = serializeFlowSvg({ ...snap, title: this._title() })
    try {
      const blob = await this._rasterize(svg, Math.round(width * this.pngScaleValue), Math.round(height * this.pngScaleValue))
      this._download(blob, "flow.png")
      this._say(`Exported flow.png — ${this._counts(snap)} at ${Math.round(width * this.pngScaleValue)}×${Math.round(height * this.pngScaleValue)}.`)
    } catch (e) {
      this._say("PNG export failed in this browser — the SVG download still works.")
    }
  }

  async copyPng() {
    const snap = this._snapshot()
    if (!snap) return
    const { width, height } = flowSvgSize(snap)
    const svg = serializeFlowSvg({ ...snap, title: this._title() })
    try {
      const blob = await this._rasterize(svg, Math.round(width * this.pngScaleValue), Math.round(height * this.pngScaleValue))
      await this._copyImage(blob, `Copied PNG to the clipboard — ${this._counts(snap)}.`)
    } catch (e) {
      this._say("PNG export failed in this browser — the SVG download still works.")
    }
  }

  async downloadCard() {
    const snap = this._snapshot()
    if (!snap) return
    const card = this._cardSvg(snap)
    try {
      const blob = await this._rasterize(card, CARD_WIDTH, CARD_HEIGHT)
      this._download(blob, "flow-card.png")
      this._say(`Exported flow-card.png — ${CARD_WIDTH}×${CARD_HEIGHT} share card.${this._gateSuffix("card", snap, card)}`)
    } catch (e) {
      this._say("Share-card export failed in this browser — the SVG download still works.")
    }
  }

  async copyCard() {
    const snap = this._snapshot()
    if (!snap) return
    try {
      const blob = await this._rasterize(this._cardSvg(snap), CARD_WIDTH, CARD_HEIGHT)
      await this._copyImage(blob, `Copied the ${CARD_WIDTH}×${CARD_HEIGHT} share card to the clipboard.`)
    } catch (e) {
      this._say("Share-card export failed in this browser — the SVG download still works.")
    }
  }

  // ── Snapshot: engine data + geometry reads + resolved tokens ─────────
  //
  // Iteration sources are the DOM contract anchors: node elements carry
  // data-ui--flow-node="<id>", visible edge paths carry data-edge="<id>"
  // (the interaction path uses data-edge-interaction and never matches).
  // The serializer then iterates the toObject() DEFS — the DOM only fills
  // geometry and paint, so ordering stays authored and deterministic.
  _snapshot(silent = false) {
    if (!this.hasUiFlowOutlet) {
      if (!silent) this._say("Engine not ready yet — try again in a moment.")
      return null
    }
    const flow = this.uiFlowOutlet
    const { nodes, edges } = flow.toObject()
    const root = flow.element

    const nodeBoxes = {}
    const nodeStyles = {}
    root.querySelectorAll("[data-ui--flow-node]").forEach((el) => {
      const id = el.getAttribute("data-ui--flow-node")
      const cs = getComputedStyle(el)
      // Hidden nodes paint nothing — the engine's own _nodeVisible predicate
      // (computed display, what fitView/minimap use), NOT the def flag: SSR
      // hidden nodes reach the DOM as class+attribute without a def write.
      if (cs.display === "none") return
      // offsetWidth is the real measurement in a browser; the inline-style
      // fallback keeps headless runs (jsdom reports 0) on the same path.
      const width = el.offsetWidth || parseFloat(el.style.width) || 0
      const height = el.offsetHeight || parseFloat(el.style.height) || 0
      if (!width || !height) return // never laid out — nothing paints
      nodeBoxes[id] = {
        x: parseFloat(el.style.left) || 0,
        y: parseFloat(el.style.top) || 0,
        width,
        height
      }
      const labelEl = el.querySelector(".ui-flow-node__header") || el.querySelector(".ui-flow-node__body") || el
      nodeStyles[id] = {
        // Type tint: TYPE_CHROME utilities put the type color on the node's
        // border (input → border-success/60, output → border-primary/60) —
        // resolve the LIVE computed value, no hardcoded type→hex map.
        tint: cs.borderColor,
        background: cs.backgroundColor,
        textColor: getComputedStyle(labelEl).color
      }
    })

    const edgePaths = {}
    root.querySelectorAll(".ui-flow-edges path[data-edge]").forEach((path) => {
      const d = path.getAttribute("d")
      if (!d) return
      const cs = getComputedStyle(path)
      if (cs.display === "none" || cs.stroke === "none") return // hidden edge
      edgePaths[path.getAttribute("data-edge")] = {
        d,
        stroke: cs.stroke,
        strokeWidth: parseFloat(cs.strokeWidth) || 1.5
      }
    })

    if (Object.keys(nodeBoxes).length === 0) {
      if (!silent) this._say("Nothing to export — no visible nodes.")
      return null
    }
    const bg = this._canvasBackground(root)
    return { nodes, edges, nodeBoxes, nodeStyles, edgePaths, background: bg.color, backgroundOpacity: bg.opacity }
  }

  // The opposite theme's snapshot, resolved on the SAME live elements by
  // flipping the root theme marker synchronously and restoring it in
  // finally — before any paint can observe the intermediate state. A
  // subtree probe cannot do this honestly: :root token values inherit
  // DOWN, so a light probe under a dark root would still read the dark
  // values. Both markers flip (.dark class per the vendored preset and
  // data-theme for hosts that use the attribute), exactly as a host's
  // theme-switcher would. Silent: the dual export's own readout speaks.
  _oppositeSnapshot() {
    const html = document.documentElement
    const hadDark = html.classList.contains("dark")
    const dataTheme = html.getAttribute("data-theme")
    try {
      if (hadDark) html.classList.remove("dark")
      else html.classList.add("dark")
      if (dataTheme !== null) html.setAttribute("data-theme", hadDark ? "light" : "dark")
      return this._snapshot(true)
    } finally {
      if (hadDark) html.classList.add("dark")
      else html.classList.remove("dark")
      if (dataTheme !== null) html.setAttribute("data-theme", dataTheme)
    }
  }

  _cardSvg(snap) {
    const foreground = this._tokenPaint("--foreground")
    const muted = this._tokenPaint("--muted-foreground")
    return serializeShareCard({
      ...snap,
      foreground: foreground.color,
      foregroundOpacity: foreground.opacity,
      mutedForeground: muted.color,
      mutedForegroundOpacity: muted.opacity,
      title: this._title(),
      subtitle: this._subtitle()
    })
  }

  // Opt-in render-quality gate readout suffix (validate value): PASS is
  // quiet, FAIL lists every reason the pure validator returned.
  _gateSuffix(kind, snap, artifact) {
    if (!this.validateValue) return ""
    const gate = kind === "card"
      ? validateShareCard(artifact, { ...snap, title: this._title(), subtitle: this._subtitle() })
      : validateFlowSvg(artifact, snap)
    return gate.ok ? " Gate: PASS." : ` Gate: FAIL — ${gate.reasons.join("; ")}.`
  }

  // The validateExport action's report half, factored so a corrupted live
  // snapshot can be re-reported from the console/eval (the scenario's RED
  // demo) without shipping a deliberately-broken fixture.
  _reportGate(snap, kind = "flow") {
    const artifact = kind === "card"
      ? this._cardSvg(snap)
      : serializeFlowSvg({ ...snap, title: this._title() })
    const gate = kind === "card"
      ? validateShareCard(artifact, { ...snap, title: this._title(), subtitle: this._subtitle() })
      : validateFlowSvg(artifact, snap)
    this._say(gate.ok
      ? `Quality gate: PASS — ${this._counts(snap)}, tokens resolved, viewBox fits.`
      : `Quality gate: FAIL — ${gate.reasons.join("; ")}.`)
  }

  _counts(snap) {
    return `${Object.keys(snap.nodeBoxes).length} nodes, ${Object.keys(snap.edgePaths).length} edges`
  }

  _title() {
    if (this.hasTitleValue && this.titleValue.trim()) return this.titleValue.trim()
    return (document.title || "Flow export").trim()
  }

  // Card subtitle "from meta" (the archify card contract): the explicit
  // value wins; otherwise the page's <meta name="description">.
  _subtitle() {
    if (this.hasSubtitleValue && this.subtitleValue.trim()) return this.subtitleValue.trim()
    return document.querySelector('meta[name="description"]')?.getAttribute("content")?.trim() || ""
  }

  // ── Token resolution (contract §colors: no hex/rgba literals — parse
  // any computed color's alpha numerically, wrap bare HSL channels in
  // hsl(), fall back to the canvas token when the canvas itself is
  // transparent). Same grammar as chart_controller/the download fixture.
  _hasAlpha(color) {
    if (!color || color === "transparent") return false
    const open = color.indexOf("(")
    if (open === -1) return true // keyword/hex: fully opaque
    const parts = color.slice(open + 1, color.lastIndexOf(")")).split(",")
    return parts.length < 4 || parseFloat(parts[3]) > 0
  }

  // Alpha-bearing tokens (wave-3 NIT): "H S% L% / A" in space OR comma form
  // splits into the color plus a separate opacity, so the serializer emits
  // a valid hsl() fill + fill-opacity/stroke-opacity attributes instead of
  // passing the raw 5-part channel string through (an invalid paint that
  // renderers silently ignore). Three channels → plain color, no opacity.
  _tokenPaint(name) {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
    if (!raw) return { color: "hsl(0 0% 100%)", opacity: null }
    const parts = raw.split(/\s*,\s*|\s+/).filter((p) => p && p !== "/")
    if ((parts.length === 3 || parts.length === 4) && parts.every((c) => /^\d+(?:\.\d+)?%?$/.test(c))) {
      return {
        color: `hsl(${parts.slice(0, 3).join(" ")})`,
        opacity: parts.length === 4 ? parseFloat(parts[3]) : null
      }
    }
    return { color: raw, opacity: null }
  }

  _tokenColor(name) {
    return this._tokenPaint(name).color
  }

  _canvasBackground(root) {
    const bg = getComputedStyle(root).backgroundColor
    return this._hasAlpha(bg) ? { color: bg, opacity: null } : this._tokenPaint("--background")
  }

  // ── Raster + delivery ────────────────────────────────────────────────

  // Camera endpoints for the recorded raster: focus = the first visible
  // node's center, mapped flow → raster px the same way the serializer
  // frames the content (translate pad − bounds, then the pngScale raster).
  _cameraEndpoints(snap, canvas) {
    const bounds = contentBounds(snap.nodes, snap.nodeBoxes)
    let focusX, focusY
    const first = (snap.nodes || []).find((def) => !def.hidden && snap.nodeBoxes?.[def.id])
    if (bounds && first) {
      const box = snap.nodeBoxes[first.id]
      focusX = (box.x + EXPORT_PADDING - bounds.x + box.width / 2) * this.pngScaleValue
      focusY = (box.y + EXPORT_PADDING - bounds.y + box.height / 2) * this.pngScaleValue
    }
    return cameraEndpoints({ width: canvas.width, height: canvas.height, focusX, focusY })
  }

  // SVG → decoded Image (same blob-URL path _rasterize uses; the export is
  // self-contained, so the canvas never taints). The WebM pipeline draws
  // this one decoded image per camera frame.
  _loadImage(svg) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }))
      const img = new Image()
      img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error("SVG rasterize failed"))
      }
      img.src = url
    })
  }

  // Record the scripted camera ease: fixed-duration wall-clock sampling of
  // the pure cameraPath (frame math inputs are deterministic; the video
  // bytes themselves are encoder output, never asserted byte-wise).
  _record(canvas, ctx, img, { from, to }) {
    return new Promise((resolve, reject) => {
      const mime = ["video/webm;codecs=vp9", "video/webm"].find((m) => MediaRecorder.isTypeSupported(m)) || ""
      const stream = canvas.captureStream(WEBM_FPS)
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
      const chunks = []
      recorder.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data) }
      recorder.onerror = () => reject(recorder.error || new Error("MediaRecorder failed"))
      recorder.onstop = () => resolve(new Blob(chunks, { type: mime || "video/webm" }))
      const draw = (t) => {
        const cam = cameraPath(t, from, to)
        // The raster already upscales the SVG by pngScale; the camera
        // transform rides on top, keeping the focus point at (cx, cy).
        const s = cam.scale
        const tx = canvas.width / 2 - s * cam.cx
        const ty = canvas.height / 2 - s * cam.cy
        ctx.setTransform(s, 0, 0, s, tx, ty)
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      }
      const start = performance.now()
      const tick = () => {
        const t = (performance.now() - start) / WEBM_DURATION_MS
        draw(Math.min(t, 1))
        if (t < 1) requestAnimationFrame(tick)
        else recorder.stop()
      }
      draw(0)
      recorder.start()
      requestAnimationFrame(tick)
    })
  }

  // SVG → Image → canvas → PNG Blob. The SVG string carries no external
  // references (colors are resolved, fonts are the system stack), so the
  // blob-URL image is same-origin safe and the canvas never taints.
  _rasterize(svg, width, height) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }))
      const img = new Image()
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas")
          canvas.width = width
          canvas.height = height
          const ctx = canvas.getContext("2d")
          if (!ctx) {
            URL.revokeObjectURL(url)
            reject(new Error("2D canvas unavailable"))
            return
          }
          ctx.drawImage(img, 0, 0, width, height)
          canvas.toBlob((blob) => {
            URL.revokeObjectURL(url)
            if (blob) resolve(blob)
            else reject(new Error("PNG encode failed"))
          }, "image/png")
        } catch (e) {
          URL.revokeObjectURL(url)
          reject(e)
        }
      }
      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error("SVG rasterize failed"))
      }
      img.src = url
    })
  }

  _download(blob, filename) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  // Feature-detect (wave-3 NIT): navigator.clipboard alone is not enough —
  // browsers can expose the object without a callable .write, so the honest
  // unsupported message must check the method itself.
  async _copyImage(blob, successMessage) {
    if (!navigator.clipboard || typeof navigator.clipboard.write !== "function" || typeof ClipboardItem === "undefined") {
      this._say("Clipboard image copy is not supported in this browser — use the download button.")
      return
    }
    try {
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })])
      this._say(successMessage)
    } catch (e) {
      this._say("Clipboard write failed — the browser denied it; use the download button.")
    }
  }

  _say(message) {
    if (this.hasReadoutTarget) this.readoutTarget.textContent = message
  }
}
